import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Droplets, Clock, ShieldCheck, Car, ChevronDown } from "lucide-react";
import { useVehicle } from "@/contexts/VehicleContext";

const HERO_BG = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600&q=80&auto=format&fit=crop";


const TRUST_ITEMS = [
  {
    id: "deplacement",
    icon: MapPin,
    title: "Déplacement offert",
    sub: "Jusqu'à 20 km autour de Versailles",
    detail: "Nous nous déplaçons gratuitement dans toute la zone Versailles + 20 km. Au-delà, des frais de déplacement modérés peuvent s'appliquer — contactez-nous pour confirmer.",
  },
  {
    id: "autonome",
    icon: Droplets,
    title: "100% autonome",
    sub: "Eau & électricité fournies",
    detail: "Nos équipements professionnels sont entièrement autonomes : pas besoin de prise électrique ni de point d'eau chez vous. Idéal pour parking, rue ou cour fermée.",
  },
  {
    id: "pro",
    icon: ShieldCheck,
    title: "Service professionnel",
    sub: "Produits & matériel haut de gamme",
    detail: "Nous utilisons des shampoings, cires et traitements de qualité concession. Microfibres dédiées par zone, machines professionnelles, gants de pH neutre pour vos jantes.",
  },
  {
    id: "rapide",
    icon: Clock,
    title: "Réservation rapide",
    sub: "Créneaux flexibles 7j/7",
    detail: "Réservation en ligne en moins d'une minute. Confirmation par téléphone sous quelques heures. Possibilité de créneaux le samedi.",
  },
];

export default function Hero() {
  const { category, categoryData, openSelector } = useVehicle();
  const [openItem, setOpenItem] = useState(null);

  return (
    <section id="top" className="relative min-h-[100vh] flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Voiture de sport premium"
          className="w-full h-full object-cover object-center scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/50"></div>
      </div>

      <div className="absolute inset-0 opacity-[0.12] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(229,9,20,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(229,9,20,0.08) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      ></div>

      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#E50914]/15 rounded-full blur-[160px] pointer-events-none animate-glow"></div>
      <div className="absolute left-[-10%] bottom-[-10%] w-[420px] h-[420px] bg-[#E50914]/8 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 w-full">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-[#E50914]/40 rounded-full mb-8 backdrop-blur-sm animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <span className="w-2 h-2 bg-[#E50914] rounded-full pulse-red"></span>
            <span className="text-xs uppercase tracking-[0.25em] text-white/90 font-semibold">
              Nettoyage premium à domicile · Versailles
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.95] mb-6 animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <span className="block text-white">Auto, maison & surfaces.</span>
            <span className="block text-gradient-red">Le propre à domicile.</span>
          </h1>

          <p className="text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed mb-10 animate-fade-up" style={{ animationDelay: '0.5s' }}>
            Nova Clean Service se déplace directement chez vous avec un service
            <span className="text-white font-semibold"> 100% autonome en eau et en électricité</span>. Un résultat de concession, sans quitter votre domicile.
          </p>

          <div className="flex flex-wrap gap-4 mb-10 animate-fade-up" style={{ animationDelay: '0.7s' }}>
            <Link
              to="/nettoyage#reserver"
              data-testid="hero-reserve-button"
              className="btn-sheen group inline-flex items-center gap-3 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-sm tracking-wider px-8 py-4 rounded-lg border border-[#E50914] shadow-[0_8px_30px_-8px_rgba(229,9,20,0.6)] hover:shadow-[0_0_36px_rgba(229,9,20,0.6)] transition-all"
            >
              Réserver maintenant
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-quote-button"
              className="inline-flex items-center gap-3 bg-white/[0.03] border border-white/20 hover:bg-white/[0.08] hover:border-white/40 text-white font-semibold uppercase text-sm tracking-wider px-8 py-4 rounded-lg transition-all"
            >
              Demander un devis
            </Link>
          </div>

          <div className="mb-12 animate-fade-up" style={{ animationDelay: '0.85s' }}>
            {category ? (
              <button
                onClick={openSelector}
                data-testid="hero-vehicle-chip"
                className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/5 border border-white/15 hover:border-[#E50914]/50 backdrop-blur-sm transition-all group"
              >
                <span className="w-6 h-6 rounded-full bg-[#E50914] flex items-center justify-center text-[11px] font-black text-white">{category}</span>
                <span className="text-sm text-white/80 group-hover:text-white">
                  <span className="font-semibold">{categoryData.name}</span>
                  <span className="text-white/40 ml-2 text-xs">· changer</span>
                </span>
              </button>
            ) : (
              <button
                onClick={openSelector}
                data-testid="hero-select-vehicle"
                className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#E50914]/10 border border-[#E50914]/40 hover:bg-[#E50914]/20 backdrop-blur-sm transition-all"
              >
                <Car className="w-4 h-4 text-[#E50914]" />
                <span className="text-sm text-white/90 font-medium">
                  Sélectionnez votre véhicule pour voir les tarifs exacts
                </span>
                <ArrowRight className="w-4 h-4 text-[#E50914]" />
              </button>
            )}
          </div>

          {/* Trust strip — wider, expandable */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 animate-fade-up" style={{ animationDelay: '0.9s' }}>
            {TRUST_ITEMS.map((it) => (
              <TrustItem
                key={it.id}
                item={it}
                open={openItem === it.id}
                onToggle={() => setOpenItem(openItem === it.id ? null : it.id)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none"></div>
    </section>
  );
}

function TrustItem({ item, open, onToggle }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onToggle}
      data-testid={`trust-${item.id}`}
      className={`group text-left p-4 rounded-xl backdrop-blur-sm border transition-all
        ${open ? 'bg-[#E50914]/10 border-[#E50914]/50' : 'bg-white/[0.04] border-white/10 hover:border-[#E50914]/40 hover:bg-white/[0.06]'}`}
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0 w-11 h-11 rounded flex items-center justify-center bg-[#E50914]/10 border border-[#E50914]/30">
          <Icon className="w-5 h-5 text-[#E50914]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-white">{item.title}</div>
          <div className="text-xs text-white/55 mt-0.5">{item.sub}</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-white/40 group-hover:text-[#E50914] transition-all shrink-0 ${open ? 'rotate-180 text-[#E50914]' : ''}`} />
      </div>
      {open && (
        <div className="mt-3 pl-14 pr-2 text-xs text-white/70 leading-relaxed border-t border-white/10 pt-3 animate-fade-up">
          {item.detail}
        </div>
      )}
    </button>
  );
}
